import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Mail, MessageCircle, Pencil, Paperclip, FileDown, X } from "lucide-react";
import { productTypes, PRICING_DATA_FRONT } from "@/data/adminData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function QuoteModal({ simulation, onClose, onSuccess }) {
  const [quoteData, setQuoteData] = useState({
    product_type: "enseigne_lumineuse",
    estimated_price: 2500,
    description: productTypes.find(t => t.value === "enseigne_lumineuse")?.desc || ""
  });
  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentData, setAttachmentData] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const handleAttachmentUpload = async (file) => {
    if (!file) { setAttachmentFile(null); setAttachmentData(null); return; }
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/quotes/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachmentFile(file);
      setAttachmentData(res.data);
      toast.success(`Fichier "${file.name}" uploadé`);
    } catch {
      toast.error("Erreur lors de l'upload");
      setAttachmentFile(null);
      setAttachmentData(null);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const generateQuote = async () => {
    try {
      const response = await axios.post(
        `${API}/quotes/generate/${simulation.id}`,
        { simulation_id: simulation.id, ...quoteData },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_${simulation.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Devis généré et téléchargé !");
      onSuccess();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la génération du devis");
    }
  };

  const sendQuoteByEmail = async (sendToClient = true) => {
    setIsSendingQuote(true);
    try {
      const res = await axios.post(
        `${API}/quotes/send-email/${simulation.id}`,
        { simulation_id: simulation.id, ...quoteData, send_to_client: sendToClient, attachment_id: attachmentData?.attachment_id || null }
      );
      toast.success(res.data.message);
      onClose();
      onSuccess();
    } catch {
      toast.error("Erreur lors de l'envoi du devis");
    } finally {
      setIsSendingQuote(false);
    }
  };

  const sendQuoteViaWhatsApp = async () => {
    setIsSendingWhatsApp(true);
    try {
      const res = await axios.post(
        `${API}/quotes/create/${simulation.id}`,
        { simulation_id: simulation.id, ...quoteData }
      );
      const { quote_number, validity_date, download_path } = res.data;
      const downloadUrl = `${BACKEND_URL}${download_path}`;
      const signUrl = `${BACKEND_URL}/devis/${quote_number}`;
      const attachUrl = attachmentData ? `${BACKEND_URL}${attachmentData.download_path}` : null;
      const product = PRICING_DATA_FRONT[quoteData.product_type] || "Enseigne sur mesure";
      const finalPrice = (quoteData.estimated_price - 500) * 1.2;
      const msg = encodeURIComponent(
        `Bonjour ${simulation.name},\n\n` +
        `Suite à votre demande, voici votre devis PUB PRO :\n\n` +
        `Devis N° ${quote_number}\n` +
        `Produit: ${product}\n` +
        `Total TTC: ${finalPrice.toFixed(0)}€ (remise -500€ HT incluse)\n` +
        `Validité: ${validity_date}\n\n` +
        `Installation comprise\nGarantie décennale\n\n` +
        `Télécharger votre devis PDF :\n${downloadUrl}\n\n` +
        (attachUrl ? `Document joint :\n${attachUrl}\n\n` : '') +
        `Signer votre devis en ligne :\n${signUrl}\n\n` +
        `PUB PRO - simulation-enseigne.fr`
      );
      const phone = simulation.phone.replace(/\s+/g, '').replace(/^0/, '33');
      window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
      toast.success("Devis généré ! WhatsApp ouvert.");
      onClose();
      onSuccess();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la génération du devis");
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#121212] border border-[#262626] rounded-3xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Générer un devis</h3>
        <p className="text-[#A3A3A3] text-sm mb-6">
          Client: <span className="text-white">{simulation.name}</span> — {simulation.city}
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Type de produit</label>
            <select
              value={quoteData.product_type}
              onChange={(e) => {
                const selected = productTypes.find(t => t.value === e.target.value);
                setQuoteData({
                  ...quoteData, 
                  product_type: e.target.value,
                  estimated_price: selected ? selected.price : quoteData.estimated_price,
                  description: selected ? selected.desc : quoteData.description
                });
              }}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 text-white"
              data-testid="quote-product-select"
            >
              {productTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label} — {type.price}€ HT
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Sélection rapide</label>
            <div className="flex flex-wrap gap-2">
              {productTypes.slice(0, 8).map(type => (
                <button
                  key={type.value}
                  onClick={() => setQuoteData({
                    ...quoteData,
                    product_type: type.value,
                    estimated_price: type.price,
                    description: type.desc
                  })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    quoteData.product_type === type.value
                      ? 'bg-[#EFFF00] text-black'
                      : 'bg-[#1a1a1a] text-[#A3A3A3] hover:bg-[#262626] border border-[#262626]'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Prix estimé HT (€)</label>
            <Input
              type="number"
              value={quoteData.estimated_price}
              onChange={(e) => setQuoteData({...quoteData, estimated_price: parseFloat(e.target.value) || 0})}
              className="bg-[#1a1a1a] border-[#262626] text-white"
              data-testid="quote-price-input"
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-[#666]">
                Après remise: <span className="text-[#EFFF00] font-bold">{(quoteData.estimated_price - 500).toFixed(0)}€ HT</span>
              </p>
              <p className="text-xs text-[#666]">
                TTC: <span className="text-white font-bold">{((quoteData.estimated_price - 500) * 1.2).toFixed(0)}€</span>
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Détail de la prestation <span className="text-[#666]">(modifiable — remplacez les ... par les dimensions)</span></label>
            <textarea
              value={quoteData.description}
              onChange={(e) => setQuoteData({...quoteData, description: e.target.value})}
              rows={8}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 text-white resize-y text-sm leading-relaxed font-mono"
              placeholder="Description du projet..."
              data-testid="quote-description"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2 flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              Pièce jointe (optionnel)
            </label>
            <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3">
              {!attachmentFile ? (
                <label className="flex items-center justify-center gap-2 cursor-pointer text-[#A3A3A3] hover:text-white transition-colors py-2">
                  <Paperclip className="w-4 h-4" />
                  <span className="text-sm">{uploadingAttachment ? 'Upload en cours...' : 'Ajouter un fichier (PDF, image...)'}</span>
                  <input
                    type="file" className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => e.target.files[0] && handleAttachmentUpload(e.target.files[0])}
                    disabled={uploadingAttachment}
                    data-testid="attachment-input"
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-[#EFFF00]" />
                    <span className="text-white text-sm truncate max-w-[200px]">{attachmentFile.name}</span>
                    <span className="text-[#666] text-xs">({(attachmentFile.size / 1024).toFixed(0)} Ko)</span>
                  </div>
                  <button
                    onClick={() => { setAttachmentFile(null); setAttachmentData(null); }}
                    className="text-red-400 hover:text-red-300 text-xs"
                    data-testid="remove-attachment-btn"
                  >
                    Retirer
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button onClick={onClose} variant="outline" className="flex-1 border-[#262626] text-[#A3A3A3] hover:bg-[#262626]">
            Annuler
          </Button>
          <Button onClick={generateQuote} className="flex-1 bg-[#EFFF00] text-black hover:bg-[#d4e000]" data-testid="quote-download-btn">
            <Download className="w-4 h-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>
        
        <div className="border-t border-[#262626] pt-4 mt-2 space-y-3">
          <p className="text-[#A3A3A3] text-xs font-medium uppercase tracking-wider">Envoyer le devis</p>
          <div className="flex gap-3">
            <Button
              onClick={() => sendQuoteByEmail(true)}
              disabled={isSendingQuote}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              data-testid="quote-send-email-btn"
            >
              {isSendingQuote ? (
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Envoi...
                </span>
              ) : (
                <span className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email + PDF</span>
              )}
            </Button>
            <Button
              onClick={sendQuoteViaWhatsApp}
              disabled={isSendingWhatsApp}
              className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white disabled:opacity-50"
              data-testid="quote-send-whatsapp-btn"
            >
              {isSendingWhatsApp ? (
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Génération...
                </span>
              ) : (
                <span className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp + PDF</span>
              )}
            </Button>
          </div>
          <p className="text-[#555] text-xs">Email : envoie le PDF en pièce jointe | WhatsApp : envoie le lien de téléchargement du PDF</p>
        </div>
      </div>
    </div>
  );
}

export function QuotesSection({ simulationId, onEditQuote }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!simulationId) return;
    setLoading(true);
    axios.get(`${API}/quotes/by-simulation/${simulationId}`)
      .then(res => { setQuotes(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [simulationId]);

  if (loading || quotes.length === 0) return null;

  return (
    <div className="pt-4 border-t border-[#262626]">
      <p className="text-sm text-[#A3A3A3] mb-3 flex items-center gap-2">
        <FileDown className="w-4 h-4" />
        Devis enregistrés ({quotes.length})
      </p>
      <div className="space-y-2">
        {quotes.map((q, i) => (
          <div key={i} className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">{q.quote_number}</p>
                <p className="text-[#666] text-xs mt-0.5">{q.quote_date} - {q.estimated_price}€ HT</p>
              </div>
              <div className="flex items-center gap-1.5">
                {q.sent_by_email && <Mail className="w-3.5 h-3.5 text-blue-400" title="Envoyé par email" />}
                <button onClick={() => onEditQuote(q)} className="text-[#A3A3A3] hover:text-[#EFFF00] transition-colors" title="Modifier ce devis" data-testid={`edit-quote-${q.quote_number}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <a href={`${API}/quotes/download/${q.quote_number}`} target="_blank" rel="noopener noreferrer" className="text-[#EFFF00] hover:text-[#d4e300]" data-testid={`download-quote-${q.quote_number}`}>
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
